import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Languages, Menu, X } from "lucide-react";
import { LOGO_URL } from "../constants";
import { LANGS, useLang } from "../i18n";
import { useQuote } from "../quote";

const LINKS = [
  { href: "#collection", key: "nav.collection" },
  { href: "#story", key: "nav.story" },
  { href: "#wholesale", key: "nav.wholesale" },
  { href: "#franchising", key: "nav.franchising" },
];

function LangMenu() {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const current = LANGS.find((l) => l.code === lang);
  return (
    <div className="relative">
      {open && <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />}
      <button
        data-testid="lang-switch"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 border border-gold/30 px-3 py-2 text-xs tracking-[0.2em] text-gold transition-colors hover:bg-gold/10"
      >
        <Languages size={14} />
        {current?.label}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="absolute end-0 z-50 mt-2 w-44 border border-gold/25 bg-cellar/95 backdrop-blur-xl"
          >
            {LANGS.map((l) => (
              <button
                key={l.code}
                data-testid={`lang-option-${l.code}`}
                onClick={() => {
                  setLang(l.code);
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between px-4 py-3 text-xs tracking-[0.15em] transition-colors hover:bg-gold/10 ${
                  l.code === lang ? "text-gold" : "text-parchment/70"
                }`}
              >
                <span>{l.native}</span>
                <span className="text-stone">{l.label}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Nav() {
  const { t } = useLang();
  const { openDrawer, count } = useQuote();
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  const raf = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      cancelAnimationFrame(raf.current);
      raf.current = requestAnimationFrame(() => setScrolled(window.scrollY > 40));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled ? "border-b border-gold/15 bg-obsidian/85 py-3 backdrop-blur-xl" : "py-5"
        }`}
      >
        <div className="flex items-center justify-between px-6 md:px-12">
          <a href="#top" data-testid="nav-logo" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="III ROMA" className="h-12 w-12 rounded-full border border-gold/50 object-cover" />
            <span className="text-carved-gold font-display whitespace-nowrap text-sm font-black tracking-[0.25em] sm:text-base sm:tracking-[0.35em]">III ROMA</span>
          </a>
          <nav className="hidden items-center gap-10 lg:flex">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.href.slice(1)}`}
                className="text-[0.68rem] uppercase tracking-[0.28em] text-parchment/70 transition-colors hover:text-gold"
              >
                {t(l.key)}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <LangMenu />
            <button
              data-testid="quote-open-button"
              onClick={openDrawer}
              className="btn-outline-gold relative !hidden !px-5 !py-2.5 !text-[0.65rem] sm:!inline-flex"
            >
              {t("nav.request")}
              {count > 0 && (
                <span className="absolute -end-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-gold text-[0.6rem] font-bold text-obsidian">
                  {count}
                </span>
              )}
            </button>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setMenu(true)}
              className="text-parchment lg:hidden"
              aria-label="Menu"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menu && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex flex-col bg-obsidian/95 backdrop-blur-xl"
          >
            <div className="flex items-center justify-between px-6 py-5">
              <span className="font-display text-sm tracking-[0.4em] text-gold">III ROMA</span>
              <button data-testid="mobile-menu-close" onClick={() => setMenu(false)} aria-label="Close menu" className="text-parchment">
                <X size={26} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center px-8">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  data-testid={`mobile-link-${l.href.slice(1)}`}
                  onClick={() => setMenu(false)}
                  className="border-b border-gold/10 py-5 font-display text-3xl text-parchment transition-colors hover:text-gold"
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.5 }}
                >
                  {t(l.key)}
                </motion.a>
              ))}
              <motion.button
                data-testid="mobile-quote-button"
                onClick={() => {
                  setMenu(false);
                  openDrawer();
                }}
                className="btn-gold mt-10"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                {t("nav.request")}
              </motion.button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
